import {memoize, NOW_SKIP_RENDER_UNTIL_PRESENT} from '@servicenow/ui-internal';
import toPropertyName from '../toPropertyName';
import {
	COMPONENT_NAME_ATTR,
	HOIST_ATTR,
	HOIST_APPEND_TO_ATTR,
	DEFER_ATTRIBUTE,
	NOW_HOIST_PROP,
	NOW_HOIST_APPEND_TO_PROP,
	NOW_HOIST_PREPEND_TO_PROP,
	NOW_ID_PROP,
	NOW_MODE_PROP,
	NOW_COMPONENT_NAME_PROP,
	NOW_ENABLE_PROPERTY_SETTING_PREVENTION_PROP,
	NOW_METRIC_TYPES_PROP,
	NOW_METRICS_LEVEL_PROP,
	NOW_LOGGING_LEVELS_PROP,
	NOW_PLACEHOLDER_HEIGHT_PROP,
	NOW_PLACEHOLDER_WIDTH_PROP,
	NOW_PRIORITY_PROP,
	PRIORITY_LEVEL_LOW,
	PRIORITY_LEVEL_MEDIUM,
	NOW_AVOID_RENDER_PROP
} from '../../constants';
import log from '../../log';

export const mergeBehaviorProperties = memoize(mergeBehaviorPropertiesInner, {
	profileName: 'mergeBehaviorProperties',
	createCacheKey: (b, p, t) => t
});

function mergeBehaviorPropertiesInner(behaviors, properties, tagName) {
	const mergedProperties = {};
	const {length: behaviorsCount} = behaviors;
	const behaviorNames = {};

	for (const k in properties) mergedProperties[k] = properties[k];

	for (let i = 0; i < behaviorsCount; i++) {
		const {
			name,
			behavior: {properties: behaviorProperties}
		} = behaviors[i];
		for (const k in behaviorProperties) {
			if (mergedProperties[k]) {
				log(`Behavior ${name} overwrote property ${k} for ${tagName}.`, {
					tagName,
					origin: 'mergeBehaviorProperties'
				});
			}

			mergedProperties[k] = behaviorProperties[k];
			behaviorNames[k] = name;
		}
	}

	return {
		...mergedProperties,
		[toPropertyName(DEFER_ATTRIBUTE)]: {},
		[toPropertyName(HOIST_ATTR)]: {
			...(mergedProperties[toPropertyName(HOIST_ATTR)] || {}),
			reflect: true
		},
		[toPropertyName(HOIST_APPEND_TO_ATTR)]: {
			...(mergedProperties[toPropertyName(HOIST_APPEND_TO_ATTR)] || {}),
			reflect: true
		},
		[toPropertyName(COMPONENT_NAME_ATTR)]: {reflect: true}, // @temporary add component-name
		[NOW_HOIST_PROP]: {...(mergedProperties[NOW_HOIST_PROP] || {}), reflect: true},
		[NOW_HOIST_APPEND_TO_PROP]: {
			...(mergedProperties[NOW_HOIST_APPEND_TO_PROP] || {}),
			reflect: true
		},
		[NOW_HOIST_PREPEND_TO_PROP]: {
			...(mergedProperties[NOW_HOIST_PREPEND_TO_PROP] || {}),
			reflect: true
		},
		[NOW_PLACEHOLDER_HEIGHT_PROP]: {
			...(mergedProperties[NOW_PLACEHOLDER_HEIGHT_PROP] || {}),
			schema: {type: 'string'}
		},
		[NOW_PLACEHOLDER_WIDTH_PROP]: {
			...(mergedProperties[NOW_PLACEHOLDER_WIDTH_PROP] || {}),
			schema: {type: 'string'}
		},
		[NOW_ID_PROP]: {},
		[NOW_MODE_PROP]: {},
		[NOW_COMPONENT_NAME_PROP]: {},
		[NOW_ENABLE_PROPERTY_SETTING_PREVENTION_PROP]: {},
		[NOW_LOGGING_LEVELS_PROP]: {
			...(mergedProperties[NOW_LOGGING_LEVELS_PROP] || []),
			schema: {type: ['array', 'boolean'], items: {type: 'string'}}
		},
		[NOW_METRIC_TYPES_PROP]: {
			...(mergedProperties[NOW_METRIC_TYPES_PROP] || {}),
			schema: {type: 'array', items: {type: 'string'}}
		},
		[NOW_METRICS_LEVEL_PROP]: {
			...(mergedProperties[NOW_METRICS_LEVEL_PROP] || {}),
			schema: {type: 'integer', minimum: 0, maximum: 5}
		},
		[NOW_PRIORITY_PROP]: {
			...(mergedProperties[NOW_PRIORITY_PROP] || {}),
			schema: {type: 'string', enum: [PRIORITY_LEVEL_LOW, PRIORITY_LEVEL_MEDIUM]}
		},
		[NOW_AVOID_RENDER_PROP]: {
			...(mergedProperties[NOW_AVOID_RENDER_PROP] || {default: false}),
			schema: {type: 'boolean'}
		},
		[NOW_SKIP_RENDER_UNTIL_PRESENT]: {
			attrToInitPropTransformer({attributeValue}) {
				if (attributeValue && typeof attributeValue === 'string') {
					return attributeValue.split(',') || [];
				}
				return [];
			},
			schema: {type: ['array'], items: {type: 'string'}}
		}
	};
}
